import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faCamera,
  faSave,
  faTimes,
  faCheckCircle,
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import mokalengProPic from '../../utils/brandImages';

interface InstructorProfile {
  userId: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  specialization: string;
  biography: string;
  hireDate: string;
  status: number;
  profilePictureUrl: string | null;
  assignedCourseCount: number;
}

const statusMap: { [key: number]: string } = {
  0: 'Active',
  1: 'Inactive',
  2: 'On Leave',
};

export default function InstructorProfilePage() {
  const [profile, setProfile] = useState<InstructorProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);

  const { accessToken, updateProfilePicture } = useAuth();

  const [formData, setFormData] = useState({ specialization: '', biography: '' });

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/instructorprofile/me', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch profile');
      const data = await response.json();
      setProfile(data);
      setFormData({ specialization: data.specialization || '', biography: data.biography || '' });
      updateProfilePicture(data.profilePictureUrl ?? null);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setMessage({ type: 'error', text: 'Failed to load profile' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);
      const response = await fetch('/api/instructorprofile/me', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Failed to update profile');
      const data = await response.json();
      setProfile(data.data);
      setIsEditing(false);
      setMessage({ type: 'success', text: 'Profile updated successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleProfilePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingPicture(true);
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await fetch('/api/instructorprofile/me/profile-picture', {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        body: uploadData,
      });

      if (!response.ok) throw new Error('Failed to upload picture');

      const data = await response.json();
      if (data.success && profile) {
        setProfile({ ...profile, profilePictureUrl: data.profilePictureUrl });
        updateProfilePicture(data.profilePictureUrl ?? null);
        setMessage({ type: 'success', text: 'Profile picture updated' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error uploading picture:', error);
      setMessage({ type: 'error', text: 'Failed to upload picture' });
    } finally {
      setIsUploadingPicture(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full py-24">
        <FontAwesomeIcon icon={faUser} className="h-12 w-12 text-brand-600 animate-pulse" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
          <p className="text-red-700">Failed to load profile</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">Trainer/Assessor Profile</h1>
        <p className="text-sm text-brand-900/60 mt-1">Manage your personal and teaching information</p>
      </div>

      <div className="p-8 max-w-5xl">
        {message && (
          <div
            className={`mb-6 p-4 rounded-2xl flex items-center gap-3 ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            <FontAwesomeIcon icon={message.type === 'success' ? faCheckCircle : faExclamationCircle} className="h-5 w-5" />
            {message.text}
          </div>
        )}

        {/* Profile Header */}
        <div className="card-surface p-6 sm:p-8 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="flex items-center gap-6">
              <div className="relative flex-shrink-0">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-brand-gradient flex items-center justify-center text-white text-xl sm:text-2xl font-bold overflow-hidden">
                  <img src={profile.profilePictureUrl || mokalengProPic} alt="Profile" className="w-full h-full object-cover" />
                </div>
                <label className="absolute bottom-0 right-0 bg-brand-600 hover:bg-brand-700 text-white p-2 rounded-full cursor-pointer transition-colors">
                  <FontAwesomeIcon icon={faCamera} className="h-4 w-4" />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleProfilePictureUpload}
                    disabled={isUploadingPicture}
                  />
                </label>
              </div>

              <div className="flex-1 min-w-0 sm:hidden">
                <h2 className="text-lg font-bold text-brand-950 truncate">
                  {profile.firstName} {profile.lastName}
                </h2>
                <p className="text-sm text-brand-900/60 mt-1 truncate">{profile.employeeId}</p>
              </div>
            </div>

            <div className="hidden sm:block flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-brand-950 truncate">
                {profile.firstName} {profile.lastName}
              </h2>
              <p className="text-brand-900/60 mt-1">{profile.employeeId}</p>
              <p className="text-sm text-brand-900/50 mt-2 truncate">{profile.email}</p>
            </div>

            <p className="text-sm text-brand-900/50 sm:hidden truncate">{profile.email}</p>

            <div className="sm:text-right">
              <span
                className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
                  profile.status === 0
                    ? 'bg-green-100 text-green-700'
                    : profile.status === 1
                      ? 'bg-gray-100 text-gray-700'
                      : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                {statusMap[profile.status]}
              </span>
              <p className="text-xs text-brand-900/50 mt-2">{profile.assignedCourseCount} assigned courses</p>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="card-surface p-5 sm:p-8">
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">First Name</label>
                <input
                  type="text"
                  value={profile.firstName}
                  disabled
                  className="w-full px-4 py-2 bg-canvas-100 rounded-lg text-brand-900 border border-canvas-300"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Last Name</label>
                <input
                  type="text"
                  value={profile.lastName}
                  disabled
                  className="w-full px-4 py-2 bg-canvas-100 rounded-lg text-brand-900 border border-canvas-300"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-brand-950 mb-2">Email</label>
              <input
                type="email"
                value={profile.email}
                disabled
                className="w-full px-4 py-2 bg-canvas-100 rounded-lg text-brand-900 border border-canvas-300"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-brand-950 mb-2">Specialization</label>
              <input
                type="text"
                name="specialization"
                value={formData.specialization}
                onChange={handleInputChange}
                disabled={!isEditing}
                placeholder="e.g. Networking & Systems Administration"
                className={`w-full px-4 py-2 rounded-lg border ${isEditing ? 'border-canvas-300 bg-white' : 'bg-canvas-100 border-canvas-300'}`}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-brand-950 mb-2">Biography</label>
              <textarea
                name="biography"
                value={formData.biography}
                onChange={handleInputChange}
                disabled={!isEditing}
                rows={4}
                placeholder="A short bio shown to students..."
                className={`w-full px-4 py-2 rounded-lg border ${isEditing ? 'border-canvas-300 bg-white' : 'bg-canvas-100 border-canvas-300'}`}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Employee ID</label>
                <input
                  type="text"
                  value={profile.employeeId}
                  disabled
                  className="w-full px-4 py-2 bg-canvas-100 rounded-lg text-brand-900 border border-canvas-300"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Hire Date</label>
                <input
                  type="date"
                  value={profile.hireDate.split('T')[0]}
                  disabled
                  className="w-full px-4 py-2 bg-canvas-100 rounded-lg text-brand-900 border border-canvas-300"
                />
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col-reverse sm:flex-row justify-end gap-3 pt-6 border-t border-canvas-200">
            {isEditing ? (
              <>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setFormData({ specialization: profile.specialization || '', biography: profile.biography || '' });
                  }}
                  className="w-full sm:w-auto justify-center px-6 py-2 border border-canvas-300 text-brand-950 font-semibold rounded-lg hover:bg-canvas-100 transition-colors flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  disabled={isSaving}
                  className="w-full sm:w-auto justify-center px-6 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-60 flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faSave} className="h-4 w-4" />
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="w-full sm:w-auto justify-center px-6 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faUser} className="h-4 w-4" />
                Edit Profile
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
